import { Typography, Box } from '@mui/material'
import { ColStack } from 'components/atoms/container/InCardStack'
import { isAllowedFeature } from 'utils/permissionCheck'
import { useTranslation, Trans } from 'react-i18next'
import colors from 'styles/colors'
import BoxWithBackground from 'components/atoms/container/BoxWithBackground'
import { ReactComponent as NoteTakerIcon } from 'assets/images/noteTaker.svg'
import { ReactComponent as ListAltCheckIcon } from 'assets/images/listAltCheck.svg'
import { ReactComponent as TimelineIcon } from 'assets/images/timeline.svg'
import { ReactComponent as GoogleMeetIcon } from 'assets/images/googleMeet.svg'
import { ReactComponent as MicroTeamsIcon } from 'assets/images/microTeams.svg'
import { IconContainer } from 'components/atoms/Icons'
import RowStack from 'components/atoms/container/RowStack'
import ToolTipCustomized from 'components/atoms/container/ToolTipCustomized'
import { CircleNumber, ActionText } from 'components/atoms/Text'
import { MONARK_PRIVACY_POLICY_URL } from 'utils/constants'
import { FEATURE_KEYS } from 'services/constants'

const BoxWrapper = ({ children, ...otherProps }) => (
  <BoxWithBackground
    paddingX={10}
    paddingY={6}
    borderRadius={3}
    width={'100%'}
    backgroundColor={colors.Grey50}
    {...otherProps}>
    <RowStack spacing={8}>{children}</RowStack>
  </BoxWithBackground>
)

export const AnnouncementHeader = ({ icon, heading, subHeading }) => {
  return (
    <ColStack
      spacing={4}
      sx={{
        width: 309,
        height: '100%',
        justifyContent: 'flex-end'
      }}>
      {icon}

      <Typography
        sx={{
          fontSize: 40,
          fontWeight: 700,
          lineHeight: '110%',
          color: colors.GoldenYellow50
        }}>
        <Trans
          i18nKey={heading}
          components={{
            highlight: <span style={{ color: colors.GoldenYellow300 }} />
          }}
        />
      </Typography>

      {subHeading && (
        <Typography
          variant='h4'
          sx={{
            lineHeight: '130%',
            color: colors.GoldenYellow50
          }}>
          <Trans
            i18nKey={subHeading}
            components={{
              highlight: <span style={{ color: colors.GoldenYellow300 }} />
            }}
          />
        </Typography>
      )}
    </ColStack>
  )
}

export const Slide1 = () => {
  const { t } = useTranslation()
  return (
    <ColStack spacing={8}>
      <Typography variant='h3'>
        {t('announcement.oneOnOneSlides.slide1.aiPoweredNotes')}
      </Typography>
      <Typography variant='body1KindaSmall'>
        {t('announcement.oneOnOneSlides.slide1.tooOften11Slip')}
      </Typography>

      <Typography variant='h4'>
        {t('announcement.oneOnOneSlides.slide1.monarkHereToChange')}
      </Typography>
      <Typography variant='body1KindaSmall'>
        {t('announcement.oneOnOneSlides.slide1.aiPoweredAssistant')}
      </Typography>

      <Typography variant='h5'>
        {t('announcement.oneOnOneSlides.slide1.noMessyNotes')}
      </Typography>
    </ColStack>
  )
}

export const Slide2 = () => {
  const { t } = useTranslation()
  const hasLicense = isAllowedFeature(FEATURE_KEYS.ONE_ON_ONE_PAID)

  const IconText = ({ icon, head, body, showCominSoon = false }) => (
    <>
      <IconContainer icon={icon} size={40} />

      <ColStack spacing={2}>
        {showCominSoon && (
          <Typography
            variant='eyebrow2'
            color='primary'
            paddingX={3}
            paddingY={1}
            backgroundColor={colors.Purple300}
            borderRadius={3}>
            {t('announcement.comingSoon')}
          </Typography>
        )}
        <Typography variant='h4' component={'span'}>
          {head}{' '}
          <Typography
            color='textSecondary'
            lineHeight={'140%'}
            component={'span'}>
            {body}
          </Typography>
        </Typography>
      </ColStack>
    </>
  )

  return (
    <ColStack spacing={8}>
      <BoxWrapper>
        <IconText
          icon={<NoteTakerIcon />}
          head={t('announcement.oneOnOneSlides.slide2.smartSummaries')}
          body={t('announcement.oneOnOneSlides.slide2.automaticallyCaptured')}
        />
      </BoxWrapper>
      <BoxWrapper backgroundColor={colors.MutedTeal50}>
        <IconText
          icon={<ListAltCheckIcon />}
          head={t('announcement.oneOnOneSlides.slide2.actionItemReminders')}
          body={t('announcement.oneOnOneSlides.slide2.deliveredAhead')}
        />
      </BoxWrapper>
      {hasLicense && (
        <BoxWrapper backgroundColor={colors.Purple100}>
          <IconText
            icon={<TimelineIcon />}
            head={t('announcement.oneOnOneSlides.slide2.aiPoweredInsights')}
            body={t('announcement.oneOnOneSlides.slide2.surfacePatterns')}
            showCominSoon={true}
          />
        </BoxWrapper>
      )}
    </ColStack>
  )
}

export const Slide3 = () => {
  const { t } = useTranslation()
  const Index = ({ number }) => (
    <CircleNumber
      value={number}
      color={colors.MidnightTeal400}
      textColor={colors.White}
      size={40}
      variant={'body1'}
      textProps={{
        fontWeight: 700,
        lineHeight: '140%'
      }}
    />
  )
  return (
    <ColStack spacing={8}>
      <BoxWrapper>
        <Index number={1} />
        <Typography variant='h4'>
          {t('announcement.oneOnOneSlides.slide3.importRecurring11')}
        </Typography>
      </BoxWrapper>
      <BoxWrapper>
        <Index number={2} />
        <Typography variant='h4'>
          {t('announcement.oneOnOneSlides.slide3.enablenoteTaker')}
        </Typography>
      </BoxWrapper>

      <BoxWrapper
        backgroundColor={colors.White}
        border={`1px solid ${colors.Grey100}`}>
        <RowStack>
          <ColStack spacing={1} flex={1}>
            <Typography variant='h5'>
              {t('announcement.oneOnOneSlides.slide3.worksWithPopularMeeting')}
            </Typography>
            <Typography variant='body1KindaSmall' color='textSecondary'>
              {t('announcement.oneOnOneSlides.slide3.joinsMeetingsYouSelect')}
            </Typography>
          </ColStack>

          <RowStack spacing={8} width={'auto'}>
            <ToolTipCustomized toolTip={t('contact.googleMeet')}>
              <Box>
                <IconContainer icon={<GoogleMeetIcon />} size={24} />
              </Box>
            </ToolTipCustomized>
            <ToolTipCustomized toolTip={t('contact.microsoftTeams')}>
              <Box>
                <IconContainer icon={<MicroTeamsIcon />} size={24} /> 
              </Box>
            </ToolTipCustomized>
          </RowStack>
        </RowStack>
      </BoxWrapper>
    </ColStack>
  )
}

export const Slide4 = () => {
  const { t } = useTranslation()
  return (
    <ColStack spacing={8}>
      <BoxWrapper>
        <Typography component={'span'}>
          <Trans i18nKey='announcement.oneOnOneSlides.slide4.personalDataNeverShared' />
          <Typography
            variant='body1Small'
            component={'span'}
            color={'textSecondary'}>
            {t('announcement.oneOnOneSlides.slide4.surfaceHighLevelTrends')}
          </Typography>
        </Typography>
      </BoxWrapper>

      <BoxWrapper backgroundColor={colors.MutedTeal50}>
        <Typography>
          <Trans i18nKey='announcement.oneOnOneSlides.slide4.personallyIdentifiableData' />
        </Typography>
      </BoxWrapper>

      <BoxWrapper backgroundColor={colors.Purple100}>
        <Typography>
          <Trans i18nKey='announcement.oneOnOneSlides.slide4.controlNoteTaker' />
        </Typography>
      </BoxWrapper>

      <Typography
        variant='body1Small'
        color={'textSecondary'}
        style={{ marginTop: 8, marginBottom: 10 }}
        component={'span'}>
        {t('announcement.oneOnOneSlides.slide4.toolToHelpYou')}
        <ActionText
          href={MONARK_PRIVACY_POLICY_URL}
          target='_blank'
          sx={{ textDecoration: 'underline' }}>
          {t('announcement.oneOnOneSlides.slide4.privacyPolicies')}
        </ActionText>
      </Typography>
    </ColStack>
  )
}

export const Slide5 = () => {
  const { t } = useTranslation()
  const hasLicense = isAllowedFeature(FEATURE_KEYS.ONE_ON_ONE_PAID)
  return (
    <Box
      sx={{
        height: 291,
        paddingX: 4,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center'
      }}>
      <Typography variant='h2'>
        {hasLicense
          ? t('announcement.oneOnOneSlides.slide5.startUsingAiPoweredAssistant')
          : t('announcement.oneOnOneSlides.slide5.encourageYourLeader')}
      </Typography>
    </Box>
  )
}
